import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DataTable, EstadoVazio, FilterCheckboxGroup, Modal, SelectField, fmtBRL, type Column } from "@atlas/ui/web";
import type { ArquivoTrocado, RetornoConsignatariaGerado } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { downloadAuthed } from "../prefeitura/_ui";

// O RETORNO PARA A CONSIGNATÁRIA (#189). A partir de um retorno processado do
// órgão/entidade, a averbadora gera o arquivo que diz ao credor o que a folha
// fez com cada contrato nas rubricas dele — hoje o RETASS da ASSALCE: toda
// linha, inclusive a sem desconto, um contrato por linha. O painel escolhe o
// credor entre os que têm rubrica confirmada na unidade, gera, mostra os
// totais e lista o que já foi gerado, com download. Regerar depois de um
// retificador cria um registro novo; os anteriores ficam. Com desconto ainda
// a distribuir entre contratos, a API recusa e a mensagem diz quais linhas.
//
// Rubrica de consignação confirmada SEM consignatária vinculada (credor a
// identificar) não tem credor para escolher: a opção "Rubricas sem
// consignatária vinculada" abre a lista delas, e as marcadas saem juntas no
// mesmo arquivo.
//
// O FORMATO DO ARQUIVO é do credor (01/10/2026): o detalhado, de sete colunas,
// é o padrão; a ASSALCE recebe o dela, de quatro. Escolher aqui grava no
// credor e vale para os próximos arquivos. As rubricas sem consignatária saem
// sempre no padrão.

/** Valor da opção do select que troca o credor pelas rubricas sem consignatária. */
const SEM_CREDOR = "sem-credor";
type Alvo = number | typeof SEM_CREDOR;

/** As rubricas de um arquivo sem credor, pelo resumo (`641,807`) ou pela parte (`rubricas:641,807`). */
function rubricasDoArquivo(a: ArquivoTrocado): string {
  const doResumo = typeof a.resumo.rubricas === "string" ? a.resumo.rubricas : null;
  const daParte = a.parte?.startsWith("rubricas:") ? a.parte.slice("rubricas:".length) : null;
  return (doResumo ?? daParte ?? "").split(",").filter(Boolean).join(", ") || "—";
}

export function RetornoConsignatariaPainel({ unidadeId, competencia, onClose }: {
  unidadeId: number;
  competencia: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [escolhido, setEscolhido] = useState<Alvo | null>(null);
  const [rubricasMarcadas, setRubricasMarcadas] = useState<Set<string>>(new Set());
  const [ultimo, setUltimo] = useState<RetornoConsignatariaGerado | null>(null);

  const candidatosQ = useQuery({
    queryKey: ["admin", "consignatarias", "candidatos", unidadeId],
    queryFn: () => atlas.admin.consignatarias.candidatos(unidadeId),
  });
  const candidatos = candidatosQ.data?.candidatos ?? [];
  const semCredor = candidatosQ.data?.semCredor ?? [];
  const formatos = candidatosQ.data?.formatos ?? [];
  const alvo: Alvo | null =
    escolhido ?? candidatos[0]?.credorId ?? (semCredor.length > 0 ? SEM_CREDOR : null);
  const ehSemCredor = alvo === SEM_CREDOR;
  const credorAtual = typeof alvo === "number" ? candidatos.find((c) => c.credorId === alvo) : undefined;
  const formatoAtual = formatos.find((f) => f.codigo === credorAtual?.retornoLayout);

  const definirFormato = useMutation({
    mutationFn: (layoutCodigo: string) => atlas.admin.consignatarias.definirLayoutRetorno(credorAtual!.credorId, layoutCodigo),
    onSuccess: () => {
      setUltimo(null);
      void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", "candidatos", unidadeId] });
    },
  });

  const geradosQ = useQuery({
    queryKey: ["admin", "consignatarias", alvo, "retornos", unidadeId, competencia],
    queryFn: () =>
      alvo === SEM_CREDOR
        ? atlas.admin.consignatarias.listarRetornosSemCredor({ unidadeId, competencia })
        : atlas.admin.consignatarias.listarRetornos(alvo!, { unidadeId, competencia }),
    enabled: alvo !== null,
  });

  const gerar = useMutation({
    mutationFn: () =>
      alvo === SEM_CREDOR
        ? atlas.admin.consignatarias.gerarRetornoSemCredor({ unidadeId, competencia, rubricas: [...rubricasMarcadas].sort() })
        : atlas.admin.consignatarias.gerarRetorno(alvo!, { unidadeId, competencia }),
    onSuccess: (r) => {
      setUltimo(r);
      void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", alvo, "retornos"] });
      void qc.invalidateQueries({ queryKey: ["admin", "troca-arquivos"] });
    },
  });

  const urlDoArquivo = (a: ArquivoTrocado, formato: "txt" | "xlsx" = "txt"): string | null => {
    if (alvo === null) return null;
    return alvo === SEM_CREDOR
      ? atlas.admin.consignatarias.retornoSemCredorUrl(a.id, formato)
      : atlas.admin.consignatarias.retornoUrl(alvo, a.id, formato);
  };
  // So' o formato detalhado tem a versao em planilha; o da ASSALCE e' TXT por pedido dela.
  const temPlanilha = (a: ArquivoTrocado) => a.layout?.codigo === "consignataria-retorno";
  const nomeXlsx = (nome: string | null | undefined) => `${(nome ?? "retorno.txt").replace(/\.txt$/i, "")}.xlsx`;

  const colunas: Column<ArquivoTrocado>[] = [
    { key: "recebidoEm", header: "Gerado em", render: (a) => new Date(a.recebidoEm).toLocaleString("pt-BR") },
    { key: "arquivoNome", header: "Arquivo", render: (a) => a.arquivoNome },
    ...(ehSemCredor ? [{ key: "rubricas", header: "Rubricas", render: rubricasDoArquivo } satisfies Column<ArquivoTrocado>] : []),
    // A versao GRAVADA na geracao: um arquivo antigo continua dizendo em que
    // formato saiu. Codigo e versao, como na troca de arquivos: o nome do
    // layout e' longo e empurrava o "Baixar" para fora do modal.
    {
      key: "formato", header: "Formato",
      render: (a) => (a.layout ? <code style={{ fontSize: 11 }}>{a.layout.codigo} v{a.layout.versao}</code> : "—"),
    },
    { key: "linhas", header: "Linhas", render: (a) => String(a.resumo.linhas ?? "—") },
    { key: "descontado", header: "Descontado", render: (a) => fmtBRL(Number(a.resumo.descontadoCentavos ?? 0) / 100) },
    { key: "hash", header: "Hash", render: (a) => <code style={{ fontSize: 11 }}>{(a.hash ?? "").slice(0, 12)}</code> },
    {
      key: "acoes", header: "", render: (a) => {
        const url = a.arquivoKey ? urlDoArquivo(a) : null;
        const urlXlsx = a.arquivoKey && temPlanilha(a) ? urlDoArquivo(a, "xlsx") : null;
        return url ? (
          <span style={{ display: "inline-flex", gap: 4, whiteSpace: "nowrap" }}>
            <Button size="sm" variant="ghost" onClick={() => void downloadAuthed(url, a.arquivoNome)}>
              ↓ TXT
            </Button>
            {urlXlsx && (
              <Button size="sm" variant="ghost" onClick={() => void downloadAuthed(urlXlsx, nomeXlsx(a.arquivoNome))}>
                ↓ Excel
              </Button>
            )}
          </span>
        ) : null;
      },
    },
  ];

  const nadaAGerar = candidatosQ.data !== undefined && candidatos.length === 0 && semCredor.length === 0;
  const podeGerar = alvo !== null && !gerar.isPending && (!ehSemCredor || rubricasMarcadas.size > 0);

  const opcoes = [
    ...candidatos.map((c) => ({ value: String(c.credorId), label: `${c.nome} · ${c.rubricas.join(", ")}` })),
    ...(semCredor.length > 0 ? [{ value: SEM_CREDOR, label: "Rubricas sem consignatária vinculada" }] : []),
  ];

  return (
    <Modal open onClose={onClose} title={`Retorno à consignatária — ${competencia}`} size="lg">
      {/* `minmax(0, 1fr)`: sem ele a coluna do grid cresce ate a largura da
          tabela, e o modal (overflow-x hidden) corta o texto e o "Baixar". Com
          ele a tabela rola dentro da propria barra, como o DataTable preve. */}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }}>
        <p style={{ margin: 0 }}>
          O arquivo diz ao credor o que a folha do órgão/entidade fez com cada contrato nas rubricas dele neste
          retorno. Entram todas as linhas, inclusive as sem desconto (desligado, falecido, margem insuficiente),
          uma por contrato. Se algum desconto ainda precisa ser distribuído entre contratos, distribua os grupos
          de desconto da competência antes de gerar.
        </p>
        {nadaAGerar ? (
          <EstadoVazio titulo="Nenhuma rubrica de consignação confirmada nesta unidade." />
        ) : (
          <>
            <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
              <SelectField
                label="Consignatária"
                value={alvo === null ? "" : String(alvo)}
                onChange={(e) => {
                  const v = e.target.value;
                  setEscolhido(v === SEM_CREDOR ? SEM_CREDOR : Number(v));
                  setUltimo(null);
                  gerar.reset();
                  definirFormato.reset();
                }}
                options={opcoes}
              />
              {credorAtual && formatos.length > 1 && (
                <SelectField
                  label="Formato do arquivo"
                  value={credorAtual.retornoLayout}
                  disabled={definirFormato.isPending}
                  onChange={(e) => definirFormato.mutate(e.target.value)}
                  options={formatos.map((f) => ({
                    value: f.codigo,
                    // "7 colunas · padrão", "4 colunas · ASSALCE": o nome inteiro do layout nao cabe no select.
                    label: `${f.colunas.length} colunas · ${f.padrao ? "padrão" : f.nome.split(" — ")[0]}`,
                  }))}
                />
              )}
              <Button disabled={!podeGerar || definirFormato.isPending} onClick={() => gerar.mutate()}>
                {gerar.isPending ? "Gerando…" : "Gerar arquivo de retorno"}
              </Button>
            </div>
            {credorAtual && formatoAtual && (
              <span style={{ fontSize: 13, opacity: 0.8 }}>
                Colunas do arquivo de {credorAtual.nome}: {formatoAtual.colunas.join(" · ")}. Uma linha por contrato,
                sem cabeçalho. A escolha fica gravada no credor.
              </span>
            )}
            {definirFormato.isError && (
              <p role="alert">{mensagemDeErro(definirFormato.error, "Não foi possível trocar o formato do arquivo.")}</p>
            )}
            {ehSemCredor && (
              <fieldset style={{ display: "grid", gap: 8, margin: 0, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }}>
                <legend style={{ fontSize: 13, padding: "0 4px" }}>Rubricas do arquivo</legend>
                <span style={{ fontSize: 13, opacity: 0.8 }}>
                  Rubricas de consignação confirmadas sem consignatária cadastrada (credor a identificar). As marcadas
                  saem juntas no mesmo arquivo.
                </span>
                <FilterCheckboxGroup
                  options={semCredor.map((r) => ({
                    value: r.codigoVerba,
                    label: [r.codigoOriginal, r.descricao, r.consignataria].filter(Boolean).join(" · "),
                  }))}
                  selected={rubricasMarcadas}
                  onChange={(next) => { setRubricasMarcadas(next); setUltimo(null); }}
                />
                {rubricasMarcadas.size === 0 && (
                  <span style={{ fontSize: 13, opacity: 0.8 }}>Marque ao menos uma rubrica para gerar.</span>
                )}
              </fieldset>
            )}
          </>
        )}
        {gerar.isError && <p role="alert">{mensagemDeErro(gerar.error, "Não foi possível gerar o retorno.")}</p>}
        {ultimo && (
          <div style={{ display: "grid", gap: 4 }}>
            <strong>
              {ultimo.arquivo.arquivoNome}: {ultimo.totais.linhas} linha(s) · {fmtBRL(ultimo.totais.descontadoCentavos / 100)} descontado
              de {fmtBRL(ultimo.totais.instruidoCentavos / 100)} instruído
              {ultimo.totais.zeradas > 0 ? ` · ${ultimo.totais.zeradas} sem desconto (0,00)` : ""}
              {ultimo.totais.semContrato > 0 ? ` · ${ultimo.totais.semContrato} sem contrato identificado` : ""}
            </strong>
            {ultimo.totais.porRubrica.map((r) => (
              <span key={r.rubrica} style={{ fontSize: 13 }}>
                Verba {r.rubrica}: {r.linhas} linha(s), {r.zeradas} sem desconto, {fmtBRL(r.descontadoCentavos / 100)} descontado
              </span>
            ))}
            {ultimo.avisos.map((a) => <span key={a} style={{ fontSize: 13, opacity: 0.8 }}>{a}</span>)}
          </div>
        )}
        {alvo !== null && (
          <DataTable
            caption="Arquivos gerados"
            columns={colunas}
            rows={geradosQ.data?.arquivos ?? []}
            rowKey={(a) => a.id}
            loading={geradosQ.isLoading}
            emptyState={
              <EstadoVazio
                titulo={ehSemCredor
                  ? "Nenhum arquivo gerado para rubricas sem consignatária nesta competência."
                  : "Nenhum arquivo gerado para este credor nesta competência."}
              />
            }
          />
        )}
      </div>
    </Modal>
  );
}
